/**
 * Normalise a video URL into an iframe-embeddable `src`.
 *
 * The `videos` content type stores a free-form `embedUrl` — authors paste a
 * YouTube watch/share link, a Vimeo page link, or an already-embeddable URL.
 * A watch/page URL fails inside `<iframe src>` (YouTube/Vimeo serve the full
 * page chrome, often blocking framing), so `VideoEmbedRenderer` runs the
 * value through `toEmbedUrl` first.
 *
 * Conversions:
 *  - `youtube.com/watch?v=ID`            -> `youtube.com/embed/ID`
 *  - `youtu.be/ID`                       -> `youtube.com/embed/ID`
 *  - `youtube.com/embed/ID`              -> passed through unchanged
 *  - `vimeo.com/ID`                      -> `player.vimeo.com/video/ID`
 *  - `player.vimeo.com/video/ID`         -> passed through unchanged
 *  - any other absolute http(s) URL      -> passed through unchanged
 *
 * Returns "" for an empty value or an unresolved `{token}` placeholder — the
 * renderer treats that as absent and renders nothing.
 */

/** True when the value is empty or still an unsubstituted `{token}` placeholder. */
function isUnresolved(value?: string | null): boolean {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return true;
  return /^\{.*\}$/.test(trimmed);
}

/** Extracts an 11-character YouTube video id from a watch / share / embed URL. */
function youTubeId(url: string): string | null {
  // youtu.be/ID
  const shortMatch = url.match(/(?:youtu\.be\/)([\w-]{11})/i);
  if (shortMatch) return shortMatch[1];
  // youtube.com/embed/ID  or  youtube.com/v/ID
  const embedMatch = url.match(/youtube(?:-nocookie)?\.com\/(?:embed|v)\/([\w-]{11})/i);
  if (embedMatch) return embedMatch[1];
  // youtube.com/watch?v=ID  (v may sit anywhere in the query string)
  const watchMatch = url.match(/[?&]v=([\w-]{11})/i);
  if (watchMatch) return watchMatch[1];
  return null;
}

/** Extracts a numeric Vimeo video id from a page / player URL. */
function vimeoId(url: string): string | null {
  const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/i);
  if (playerMatch) return playerMatch[1];
  const pageMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (pageMatch) return pageMatch[1];
  return null;
}

/**
 * Normalises a YouTube / Vimeo / generic URL to an embeddable iframe `src`.
 * Returns "" when the value is empty or an unsubstituted `{token}`.
 */
export function toEmbedUrl(embedUrl?: string | null): string {
  if (isUnresolved(embedUrl)) return "";
  const url = (embedUrl as string).trim();

  if (/(?:youtube(?:-nocookie)?\.com|youtu\.be)/i.test(url)) {
    const id = youTubeId(url);
    return id ? `https://www.youtube.com/embed/${id}` : url;
  }

  if (/vimeo\.com/i.test(url)) {
    const id = vimeoId(url);
    return id ? `https://player.vimeo.com/video/${id}` : url;
  }

  // Generic iframe-embeddable URL — passed through unchanged.
  return url;
}
